import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { SSDI_PLANS } from "../../../constants/ssdiPlans";
import SubscriptionPlansGrid from "../../../components/plans/SubscriptionPlansGrid";
import {
  decodeReturnTo,
  isValidPlanId,
  navigateWithPlanSelection,
} from "../../../utils/planNavigation";
import { loadRazorpayScript, openRazorpayCheckout } from "../../../utils/razorpayCheckout";
import repoAdminService from "../../../services/repoAdmin.service";
import useAuth from "../../../hooks/useAuth";
import { getApiErrorMessage, saveExcelBlob } from "../../../utils/downloadExcel";
import ExportExcelButton from "../../../components/common/ExportExcelButton";
import "../../../styles/plans.css";
import "../../../styles/inventoryUpdate.css";
import "../../../styles/users.css";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function paymentPlanLabel(row) {
  const type = row?.meta?.paymentType;
  if (type === "AGENCY_ACTIVATION") return "First user activation";
  if (type === "USER_CONNECT_FEE") return "User activation";
  const tier = row?.meta?.targetPlanName || row?.meta?.tierId || "";
  if (tier === "free") return "Pay Per User";
  return tier || "—";
}

export default function RepoPlans() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { auth } = useAuth();

  const rawPlan = (searchParams.get("plan") || "").toLowerCase();
  const highlightedPlanId = isValidPlanId(rawPlan) ? rawPlan : "";
  const returnTo = decodeReturnTo(searchParams);

  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [payments, setPayments] = useState([]);
  const [registrationFee, setRegistrationFee] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    if (!auth?.token) return;
    try {
      const [subRes, companyRes] = await Promise.allSettled([
        repoAdminService.getSubscription(auth.token),
        repoAdminService.getMyCompany(auth.token),
      ]);
      const subPayload =
        subRes.status === "fulfilled" ? subRes.value?.data || subRes.value : null;
      const companyPayload =
        companyRes.status === "fulfilled"
          ? companyRes.value?.data?.company ||
            companyRes.value?.data ||
            companyRes.value?.company ||
            subPayload?.company
          : subPayload?.company;

      setSubscription(subPayload?.subscription || null);
      setUsage(subPayload?.usage || null);
      setPayments(subPayload?.payments || []);

      const fromApi = subPayload?.registrationFee;
      const companyActive =
        fromApi?.companyStatus === "ACTIVE" ||
        companyPayload?.status === "ACTIVE";
      const feePaid =
        fromApi?.status === "PAID" ||
        fromApi?.paymentStatus === "PAID" ||
        companyPayload?.paymentStatus === "PAID" ||
        companyActive;
      if (!fromApi && !companyPayload) {
        setRegistrationFee(null);
      } else {
        setRegistrationFee(
          fromApi
            ? { ...fromApi, status: feePaid ? "PAID" : fromApi.status || "UNPAID" }
            : {
                status: feePaid ? "PAID" : "UNPAID",
                paymentStatus: companyPayload?.paymentStatus || null,
                companyStatus: companyPayload?.status || null,
              }
        );
      }
    } catch {
      setSubscription(null);
    }
  }, [auth?.token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDownloadPaymentsExcel = async () => {
    if (!auth?.token) return;
    setExporting(true);
    setError("");
    try {
      const response = await repoAdminService.downloadPaymentsExcel(auth.token);
      await saveExcelBlob(response, "payment-history.xlsx");
    } catch (err) {
      setError(await getApiErrorMessage(err, "Failed to download payment history Excel."));
    } finally {
      setExporting(false);
    }
  };

  const handleSelectPlan = (plan) => {
    navigateWithPlanSelection(navigate, returnTo, plan.id);
  };

  const handleCheckout = async (plan) => {
    if (returnTo) {
      handleSelectPlan(plan);
      return;
    }
    if (plan.id === "free") {
      setMessage("Pay Per User has no monthly charge. Users are billed ₹100 when you activate them.");
      setError("");
      return;
    }
    setError("");
    setMessage("");
    setBusy(true);
    try {
      await loadRazorpayScript();
      const orderRes = await repoAdminService.createSubscriptionOrder(plan.id);
      const data = orderRes?.data || orderRes || {};
      if (!data.razorpayKeyId || !data.orderId) {
        throw new Error("Payment order could not be created. Try again.");
      }
      const checkout = await openRazorpayCheckout({
        key: data.razorpayKeyId,
        orderId: data.orderId,
        amount: data.amount,
        currency: data.currency,
        description: `${plan.name} monthly subscription`,
        prefill: {
          name: auth?.user?.name || "",
          email: auth?.user?.email || "",
        },
      });
      const verifyRes = await repoAdminService.verifySubscriptionPayment({
        razorpay_order_id: checkout.razorpay_order_id,
        razorpay_payment_id: checkout.razorpay_payment_id,
        razorpay_signature: checkout.razorpay_signature,
        paymentId: data.paymentId,
      });
      const okMessage =
        verifyRes?.data?.message ||
        verifyRes?.message ||
        `Payment successful. Your ${plan.name} plan is active.`;
      setMessage(okMessage);
      await load();
    } catch (err) {
      if (err?.message === "CHECKOUT_CLOSED") {
        setError("Payment was cancelled. Your subscription has not been activated.");
      } else if (err?.message === "PAYMENT_FAILED") {
        setError("Payment failed. Your subscription has not been activated.");
      } else {
        setError(
          await getApiErrorMessage(
            err,
            "Payment failed. Your subscription has not been activated."
          )
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page ssdi-plans-page">
      <header className="ssdi-plans-header">
        <div>
          <h2>Subscription Plans</h2>
          <p className="muted">
            {returnTo
              ? "Click a plan to select it and return to your previous page."
              : "Registration is free. Pay ₹100 to submit for verification. Every user on Pay Per User is ₹100 when activated (no free user)."}
          </p>
        </div>
        {returnTo && (
          <Link to={returnTo} className="secondary-page-btn ssdi-plans-back-link">
            ← Back
          </Link>
        )}
      </header>

      {error ? (
        <p className="error-text ssdi-plan-checkout-error" role="alert">
          {error}
        </p>
      ) : null}
      {message ? <p className="inv-success">{message}</p> : null}

      {!returnTo && registrationFee ? (
        <section className="ssdi-registration-fee" aria-label="Registration fee">
          <p className="ssdi-registration-fee-label">
            Repo Agency Registration Fee (₹0 — Free)
          </p>
          <p className="ssdi-registration-fee-status">
            <strong>Registration Fee:</strong>{" "}
            {registrationFee.status === "PAID" ? (
              <span className="ssdi-registration-fee-paid">PAID</span>
            ) : (
              <span className="ssdi-registration-fee-unpaid">
                {registrationFee.status || "UNPAID"}
              </span>
            )}
          </p>
        </section>
      ) : null}

      {subscription ? (
        <section className="ssdi-plan-current">
          <p>
            <strong>Current Plan:</strong> {subscription.planName || "—"}
          </p>
          <p>
            <strong>Plan Status:</strong> {subscription.status || "—"}
          </p>
          <p>
            <strong>Start Date:</strong> {formatDate(subscription.startedAt)}
          </p>
          <p>
            <strong>Expiry Date:</strong> {formatDate(subscription.expiresAt)}
          </p>
          <p>
            <strong>Plan Duration:</strong>{" "}
            {subscription.durationDays
              ? `${subscription.durationDays} days`
              : "—"}
          </p>
          <p>
            <strong>Remaining Days:</strong> {subscription.remainingDays ?? 0}
          </p>
          <p>
            <strong>Active User Count:</strong>{" "}
            {usage?.activeUserCount ?? subscription.activeUserCount ?? 0}
          </p>
          <p>
            <strong>Included User Limit:</strong>{" "}
            {(usage?.includedUserLimit ?? subscription.includedUserLimit) == null
              ? "Unlimited"
              : usage?.includedUserLimit ?? subscription.includedUserLimit}
          </p>
          <p>
            <strong>Extra Paid Users:</strong>{" "}
            {usage?.extraPaidUsers ?? subscription.extraPaidUsers ?? 0}
          </p>
          {subscription.tierId && subscription.tierId !== "free" ? (
            <div>
              <button
                type="button"
                className="primary-page-btn"
                disabled={busy}
                onClick={() => {
                  const plan = SSDI_PLANS.find((p) => p.id === subscription.tierId);
                  if (plan) handleCheckout(plan);
                }}
              >
                Renew Plan
              </button>
            </div>
          ) : null}
        </section>
      ) : null}

      <SubscriptionPlansGrid
        highlightedPlanId={highlightedPlanId}
        returnTo={returnTo}
        plansBasePath="/plans"
        onSelectPlan={handleCheckout}
        currentTierId={subscription?.tierId || ""}
        subscriptionStatus={subscription?.status || ""}
        checkoutBusy={busy}
      />

      {!returnTo ? (
        <section className="ssdi-plan-history">
          <div className="ssdi-plan-history__head">
            <h3>Payment history</h3>
            {payments.length > 0 ? (
              <ExportExcelButton
                onClick={handleDownloadPaymentsExcel}
                loading={exporting}
              />
            ) : null}
          </div>
          {payments.length === 0 ? (
            <p className="muted">No payments yet.</p>
          ) : (
            <div className="company-table-wrap">
              <table className="users-table excel-grid-table">
                <thead>
                  <tr>
                    <th>Payment Date</th>
                    <th>Plan</th>
                    <th>Amount</th>
                    <th>Payment ID</th>
                    <th>Status</th>
                    <th>Payment Source</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((row) => (
                    <tr key={row._id || row.id}>
                      <td>{formatDate(row.paidAt || row.createdAt)}</td>
                      <td>{paymentPlanLabel(row)}</td>
                      <td>₹{Number(row.amount || 0).toLocaleString("en-IN")}</td>
                      <td>{row.razorpayPaymentId || "—"}</td>
                      <td>{row.status}</td>
                      <td>{row.paymentSource || "RAZORPAY"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}
